import { useEffect, useState } from 'react'
import { axiosInstance } from '../../../service/axiosManager'

// The server's clock: the student's clock plus an offset measured against GET /api/v1/time.
// The coding window and a delayed condition's AI unlock come from the server (started_at), so
// a student whose computer clock is a few minutes off must still see them at the right moment.
// Until the first answer comes back, the student's own clock stands in.
//
// One measurement per page, shared by every hook on it. The server reads its clock somewhere
// in the request's round trip, so the shorter the trip, the closer the offset: the page takes
// a few samples a second or two apart and keeps the one with the shortest trip (the first is
// often the slowest - it comes in the class's Start burst, behind the browser's CORS
// preflight). A failed request is tried again after about 2, 5, 10, then every 20 seconds,
// for as long as a hook is mounted. Each better offset reaches every mounted hook.

const TIME_URL = process.env.REACT_APP_HOST_API + '/time'
const TIME_TIMEOUT_MS = 8000

const SAMPLES = 3
// Between samples: one to two seconds.
const SAMPLE_GAP_MS = 1000
// After the 1st, 2nd, 3rd failure in a row, and every one after that. Jittered (as in
// SessionContext) so a class whose requests failed together does not retry in lockstep.
const RETRY_DELAYS_MS = [2000, 5000, 10000, 20000]
const retryDelay = (failures) =>
  RETRY_DELAYS_MS[Math.min(failures, RETRY_DELAYS_MS.length) - 1] * (0.75 + Math.random() * 0.5)

// A measurement of the server's clock against this computer's. fetchServerNow() resolves to the
// server's time (ms since 1970). offset() is the best offset so far (ms to add to Date.now()),
// or null before the first answer. subscribe(listener) calls listener(offset) at once if there
// is one and again with each better one, starts measuring if it has stopped, and returns the
// unsubscribe function. Measuring stops once it has its samples, and while nobody listens.
export const createServerClock = (fetchServerNow) => {
  let best = null // { offsetMs, roundTripMs } of the sample with the shortest round trip
  let samples = 0
  let failures = 0 // in a row
  let busy = false // a request in flight, or the next one waiting its turn
  const listeners = new Set()

  const measure = async () => {
    if (busy || samples >= SAMPLES || listeners.size === 0) return
    busy = true
    let improved = false
    let waitMs
    try {
      const sentAt = Date.now()
      const serverNow = Number(await fetchServerNow())
      const receivedAt = Date.now()
      if (!Number.isFinite(serverNow)) throw new Error('The answer has no time')
      samples += 1
      failures = 0
      waitMs = SAMPLE_GAP_MS * (1 + Math.random())
      const roundTripMs = receivedAt - sentAt
      if (!best || roundTripMs < best.roundTripMs) {
        // The server read its clock about halfway through the round trip.
        best = { offsetMs: serverNow - (sentAt + receivedAt) / 2, roundTripMs }
        improved = true
      }
    } catch (error) {
      failures += 1
      waitMs = retryDelay(failures)
    }
    if (samples < SAMPLES) {
      setTimeout(() => {
        busy = false
        measure()
      }, waitMs)
    } else {
      busy = false
    }
    if (improved) listeners.forEach((listener) => listener(best.offsetMs))
  }

  return {
    offset: () => (best ? best.offsetMs : null),
    subscribe: (listener) => {
      listeners.add(listener)
      if (best) listener(best.offsetMs)
      measure()
      return () => {
        listeners.delete(listener)
      }
    },
  }
}

const pageClock = createServerClock(() =>
  axiosInstance.get(TIME_URL, { timeout: TIME_TIMEOUT_MS }).then((res) => res.data?.now)
)

// { offsetMs, known }: the ms to add to Date.now() for the server's time (0 until the page has
// measured the server's clock), and whether it has. Re-renders only when a better offset
// arrives, never on a tick: for a hook that sets its own timer for a moment on the server's
// clock (useAiGate). A hook that is not enabled measures nothing and is the student's clock.
export const useServerOffset = (enabled = true) => {
  const [offset, setOffset] = useState(() => pageClock.offset())

  useEffect(() => {
    if (!enabled) return undefined
    return pageClock.subscribe(setOffset)
  }, [enabled])

  const known = enabled && offset !== null
  return { offsetMs: known ? offset : 0, known }
}

// { nowMs, known }: the server's time in ms since 1970, and whether the page has measured the
// server's clock yet (until then nowMs is the student's own clock). Re-renders every
// `intervalMs` while `enabled`, and when a better offset arrives. With intervalMs null it does
// not tick: nowMs stays at the last tick (plus the latest offset) until it ticks again. A hook
// that is not enabled measures nothing and is the student's clock as it last read it.
export const useServerClock = (enabled = true, intervalMs = 1000) => {
  const { offsetMs, known } = useServerOffset(enabled)
  const [now, setNow] = useState(() => Date.now())

  const ticking = enabled && intervalMs > 0
  useEffect(() => {
    if (!ticking) return undefined
    // Read the clock at once as well: a clock that stopped ticking would otherwise start
    // from where it stopped.
    const tick = () => setNow(Date.now())
    tick()
    const id = setInterval(tick, intervalMs)
    return () => clearInterval(id)
  }, [ticking, intervalMs])

  return { nowMs: now + offsetMs, known }
}

// The server's time in ms since 1970, re-rendering every `intervalMs` while `enabled` (see
// useServerClock).
export const useServerNow = (enabled = true, intervalMs = 1000) => useServerClock(enabled, intervalMs).nowMs
